/**
 * EstatePhoto — renders one Google Places estate photo, minted for THIS view
 * (D14: fetched per view from the browser, stored nowhere), with what Google
 * REQUIRES beside it: the author credit, and a link to the individual source
 * photo on Google Maps (Places policies, "Photos and reviews").
 *
 * Two pieces, because the credit's LINKS may not live inside a control. The
 * mosaic makes each photo a `<button>` that opens the gallery, and an `<a>`
 * inside a `<button>` is invalid HTML whose click the button swallows — the
 * policy-required Maps link would render and be unreachable. So:
 *
 *   * {@link EstatePhotoImage} is the image alone — what goes INSIDE a button;
 *   * {@link EstatePhotoCredit} is the overlay — a SIBLING of the button,
 *     absolutely positioned over the same tile by the shared `relative` parent;
 *   * {@link EstatePhoto} is both, for a tile that is not a control (the
 *     gallery dialog).
 */
import { useTranslations } from '@/shared/i18n/useTranslations';
import type { EstatePhotoData } from '@/shared/lib/types/estates';
import { plaintextString } from '@letsgethome/types/branded';
import { cn } from '@letsgethome/ui/lib/utils';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';

type Props = {
  photo: EstatePhotoData;
  alt: string;
  className?: string;
};

export function EstatePhotoImage({ photo, alt, className }: Props) {
  return (
    <img
      src={photo.uri}
      width={photo.widthPx || undefined}
      height={photo.heightPx || undefined}
      alt={alt}
      loading="lazy"
      // Google's serving host needs no referrer, and must not learn which of
      // our pages a visitor is on.
      referrerPolicy="no-referrer"
      className={className ?? 'absolute inset-0 h-full w-full object-cover'}
    />
  );
}

/**
 * The author credit and the "View on Google Maps" link, pinned to the tile's
 * bottom-right corner. It must be rendered OUTSIDE any interactive ancestor
 * (see the module doc); its parent supplies `position: relative`. It sits on
 * `Z_LAYERS.cardControls` — the layer that wins the hit test over a tile's
 * full-bleed "open the gallery" target — so its links are clickable over the
 * trigger they are drawn on.
 */
export function EstatePhotoCredit({ photo }: { photo: EstatePhotoData }) {
  const t = useTranslations('estates.detail.gallery');
  const credits = photo.attributions.filter((a) => a.displayName);
  if (credits.length === 0 && !photo.googleMapsUri) return null;
  return (
    <span
      className={cn(
        'absolute bottom-1 right-1 flex gap-1.5 rounded bg-black/50 px-1.5 py-0.5 text-[10px] text-white/90',
        Z_LAYERS.cardControls.className
      )}
    >
      {credits.map((a, i) =>
        a.uri ? (
          <a key={i} href={a.uri} target="_blank" rel="noopener noreferrer">
            {plaintextString(a.displayName)}
          </a>
        ) : (
          <span key={i}>{plaintextString(a.displayName)}</span>
        )
      )}
      {photo.googleMapsUri && (
        <a
          href={photo.googleMapsUri}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          {t('viewOnGoogleMaps')}
        </a>
      )}
    </span>
  );
}

export function EstatePhoto(props: Props) {
  return (
    <>
      <EstatePhotoImage {...props} />
      <EstatePhotoCredit photo={props.photo} />
    </>
  );
}
